'use client';

import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Stack,
  TextField,
  CircularProgress,
  Button,
  FormControlLabel,
  Switch,
  InputAdornment,
  Typography,
  IconButton
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import MapIcon from '@mui/icons-material/Map';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';

import DrawPolygon from '../../../components/DrawPolygon';
import PickerField from '../../../components/PickerField';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import { useCompanyDataList } from '../../../stores/companyStore';
import usePickerPagination from '../../../hooks/usePickerPagination';

import { createGeofence, updateGeofence, getGeofenceById } from '../../../stores/geofenceStore';
import { usePolygonTypeList } from '../../../stores/polygonTypeStore';
import {
  featureToFeatureCollection,
  geojsonToWKT,
  polygonCoordsStringFromGeoJSON,
  computeCentroidFromGeoJSON,
  wktToGeoJSON
} from '../../../utils/geo';
import { useLanguage } from '../../../utils/i18n';
import { formatUpperCase } from '../../../utils/formatTitleCase';

const MasterGeofenceLibraryAddEdit = forwardRef(
  ({ open, id = null, onClose = null, onSuccess = null, onError = null, passedWkt = null, disabledViewMap = false }, ref) => {
    const [isEditingGeom, setIsEditingGeom] = useState(false);
    const [initialValues, setInitialValues] = useState({
      company: null,
      company_name: '',
      name: '',
      geofence_code: '',
      address: '',
      remark: '',
      status: true,
      min_speed: '',
      max_speed: '',
      geom: null,
      geom_wkb: null,
      coords: null,
      centroid_lat: null,
      centroid_lon: null,
      info: null,
      latitude: null,
      longitude: null
    });

    const { t } = useLanguage();

    const [loading, setLoading] = useState(false);
    const [mapOpen, setMapOpen] = useState(false);
    const companyPickerParams = usePickerPagination({
      fetchHook: useCompanyDataList,
      buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
      searchKey: 'name'
    });

    const companyOptionsNormalized = companyPickerParams.options;

    const polygonTypePickerParams = usePickerPagination({
      fetchHook: usePolygonTypeList,
      buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
      searchKey: 'name'
    });
    const polygonTypeOptions = polygonTypePickerParams.options;

    useEffect(() => {
      try {
        if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
        const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
        if (!raw) return;
        let u = null;
        try {
          u = JSON.parse(raw);
        } catch {
          return;
        }
        const companyId = u?.company ?? u?.company_id ?? null;
        if (companyId == null) return;
      } catch (e) {
        console.error('Auto select company failed', e);
      }
    }, [companyOptionsNormalized]);
    const notify = useNotifier();

    const formikRef = useRef(null);

    useImperativeHandle(ref, () => ({
      submitForm: () => formikRef.current?.submitForm?.()
    }));

    useEffect(() => {
      if (!open) return;

      let mounted = true;
      const fetch = async () => {
        if (!id) {
          setLoading(false);

          if (passedWkt) {
            const geojsonFeature = wktToGeoJSON(passedWkt);
            if (geojsonFeature) {
              const fc = featureToFeatureCollection(geojsonFeature);
              const centroid = computeCentroidFromGeoJSON(geojsonFeature);
              const coordsStr = polygonCoordsStringFromGeoJSON(geojsonFeature);

              setInitialValues((prev) => ({
                ...prev,
                geom: passedWkt,
                info: fc,
                coords: coordsStr,
                centroid_lat: centroid.lat,
                centroid_lon: centroid.lon,
                latitude: centroid.lat,
                longitude: centroid.lon
              }));
            } else {
              setInitialValues((prev) => ({
                ...prev,
                geom: passedWkt
              }));
            }
          } else {
            setInitialValues({
              company: null,
              company_name: '',
              name: '',
              geofence_code: '',
              address: '',
              polygon_type: '',
              polygon_type_name: '',
              remark: '',
              status: true,
              max_speed: '',
              geom: null,
              geom_wkb: null,
              coords: null,
              centroid_lat: null,
              centroid_lon: null,
              info: null,
              latitude: null,
              longitude: null
            });
          }

          return;
        }
        setLoading(true);
        try {
          const data = await getGeofenceById(id);
          if (!mounted) return;
          if (data) {
            const companyVal = data.company ?? data.company_id ?? null;

            const cleanStr = (val) => (val === '-' ? '' : val);

            setInitialValues((prev) => ({
              ...prev,
              company: companyVal != null ? String(companyVal) : prev.company,
              company_name: data.company_name ?? data.join?.company_name ?? prev.company_name,
              name: cleanStr(data.name) ?? prev.name,
              geofence_code: cleanStr(data.geofence_code) ?? prev.geofence_code,
              address: cleanStr(data.address) ?? prev.address,
              polygon_type: data.polygon_type ?? '',
              polygon_type_name: cleanStr(data.polygon_type_name) ?? '',
              remark: cleanStr(data.remark) ?? '',
              status: typeof data.status === 'boolean' ? data.status : (data.is_active ?? prev.status),
              max_speed: data.max_speed ?? prev.max_speed,
              min_speed: data.min_speed ?? prev.min_speed,
              geom: cleanStr(data.geom) ?? null,
              geom_wkb: cleanStr(data.geom_wkb) ?? null,
              coords: cleanStr(data.coords) ?? null,
              centroid_lat: cleanStr(data.centroid_lat) ?? null,
              centroid_lon: cleanStr(data.centroid_lon) ?? null,
              info: cleanStr(data.info) ?? null,
              latitude: cleanStr(data.latitude) ?? cleanStr(data.centroid_lat) ?? prev.latitude,
              longitude: cleanStr(data.longitude) ?? cleanStr(data.centroid_lon) ?? prev.longitude
            }));
          }
        } catch (err) {
          console.error('Failed to load ETO:', err);
          notify({ message: 'Gagal memuat data Geofence', severity: 'error' });
          if (onError) onError();
        } finally {
          if (mounted) setLoading(false);
        }
      };
      fetch();
      return () => {
        mounted = false;
      };
    }, [id, open, notify, onError, passedWkt]);

    useEffect(() => {
      if (id || !open) return;

      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      if (!stored) return;

      if (companyOptionsNormalized && companyOptionsNormalized.length) {
        const match = companyOptionsNormalized.find((c) => String(c.value) === String(stored));
        if (match) {
          setInitialValues((prev) => {
            if (prev.company) return prev;
            return {
              ...prev,
              company: String(match.value),
              company_name: match.label ?? match.name ?? String(match.value)
            };
          });
          return;
        }
      }

      setInitialValues((prev) => {
        if (prev.company) return prev;
        return {
          ...prev,
          company: String(stored),
          company_name: String(stored)
        };
      });
    }, [companyOptionsNormalized, id, open]);

    const validationSchema = Yup.object().shape({
      company: Yup.mixed().required(t('validation.companyRequired', 'Company is required')),
      name: Yup.string().required('Name is required'),
      polygon_type: Yup.mixed().required('Geofence Type is required'),
      status: Yup.boolean().required('Status is required'),
      address: Yup.string().required(t('validation.addressRequired', 'Address is required'))
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
      try {
        if (values.company == null || values.company === '') throw new Error('Company tidak valid');

        const payload = {
          id: id ?? 0,
          company_id: values.company != null ? Number(values.company) : null,
          name: formatUpperCase(values.name) ?? '',
          geofence_code: values.geofence_code ?? null,
          address: values.address ?? '',
          polygon_type: Number(values.polygon_type) || 0,
          polygon_type_name: values.polygon_type_name ?? '',
          remark: values.remark || '-',
          status: Boolean(values.status),
          min_speed: values.min_speed ? Number(values.min_speed) : 0,
          max_speed: values.max_speed ? Number(values.max_speed) : 0,
          geom: values.geom || '-',
          geom_wkb: values.geom_wkb || '-',
          coords: values.coords || '-',
          centroid_lat: values.centroid_lat || '-',
          centroid_lon: values.centroid_lon || '-',
          info: values.info || '-',
          latitude: values.latitude || values.centroid_lat || '-',
          longitude: values.longitude || values.centroid_lon || '-'
        };
        let response;

        if (id) {
          response = await updateGeofence(id, payload);
        } else {
          response = await createGeofence(payload);
        }
        if (response?.code === 400 || response?.error === true || response?.error) {
          throw response;
        }
        notify({
          message: id ? t('notify.update', 'Updated successfully') : t('notify.create', 'Created successfully'),
          severity: 'success'
        });
        if (onSuccess) onSuccess();
        if (!id) {
          resetForm();
        }
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik) => {
            const { values, handleChange, handleBlur, setFieldValue, touched, errors, isSubmitting } = formik;
            return (
              <FormDialog
                open={open}
                onClose={onClose}
                title={id ? t('geofenceList.edit', 'Edit Geofence') : t('geofenceList.add', 'Add Geofence')}
                fullScreenMode={true}
                fullHeight={false}
                actions={
                  <>
                    <Button
                      variant="contained"
                      type="submit"
                      form="master-eto-form"
                      disabled={isSubmitting || loading}
                      startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                      sx={{
                        backgroundColor: '#0072DA',
                        textTransform: 'none',
                        borderRadius: 1,
                        px: 3,
                        fontWeight: 700,
                        boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
                      }}
                    >
                      {isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
                    </Button>
                    <Button onClick={onClose} disabled={isSubmitting} variant="outlined">
                      {t('buttonTitle.cancel', 'Cancel')}
                    </Button>
                  </>
                }
              >
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, minHeight: 200, alignItems: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
                    <form id="master-eto-form" onSubmit={formik.handleSubmit}>
                      <Box
                        sx={{ display: 'flex', gap: 4, alignItems: 'stretch', width: '100%', flexDirection: { xs: 'column', md: 'row' } }}
                      >
                        {/* KOLOM KIRI */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Stack spacing={2}>
                            <PickerField
                              label={`${t('geofenceList.company', 'Company')} *`}
                              value={values.company}
                              {...companyPickerParams}
                              options={companyOptionsNormalized}
                              onChange={(opt) => {
                                const val = typeof opt === 'object' ? opt?.value : opt;
                                const selected = companyOptionsNormalized.find((c) => String(c.value) === String(val)) ?? null;
                                const selectedId = selected ? selected.value : (val ?? null);
                                setFieldValue('company', selectedId);
                                if (selected) setFieldValue('company_name', selected.label);
                              }}
                              dialogTitle={t('general.selectCompany', 'Select Company')}
                              error={touched.company && !!errors.company}
                              helperText={touched.company && errors.company}
                            />
                            <TextField
                              label={`${t('geofenceList.name', 'Geofence Name')} *`}
                              name="name"
                              value={values.name ?? ''}
                              onChange={(e) => {
                                setFieldValue('name', formatUpperCase(e.target.value));
                              }}
                              onBlur={handleBlur}
                              size="medium"
                              fullWidth
                              error={touched.name && Boolean(errors.name)}
                              helperText={touched.name && errors.name}
                            />
                            <TextField
                              label={t('geofenceList.code', 'Geofence Code')}
                              name="geofence_code"
                              value={values.geofence_code ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              size="medium"
                              fullWidth
                            />

                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <TextField
                                label={t('geofenceList.minSpeed', 'Min Speed In Area')}
                                name="min_speed"
                                type="number"
                                value={values.min_speed ?? ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                size="medium"
                                fullWidth
                                error={touched.min_speed && Boolean(errors.min_speed)}
                                helperText={touched.min_speed && errors.min_speed}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">KM/H</InputAdornment>
                                }}
                              />
                              <TextField
                                label={t('geofenceList.maxSpeed', 'Max Speed In Area')}
                                name="max_speed"
                                type="number"
                                value={values.max_speed ?? ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                size="medium"
                                fullWidth
                                error={touched.max_speed && Boolean(errors.max_speed)}
                                helperText={touched.max_speed && errors.max_speed}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">KM/H</InputAdornment>
                                }}
                              />
                            </Box>

                            <PickerField
                              label={`${t('geofenceList.type', 'Geofence Type')} *`}
                              value={values.polygon_type}
                              {...polygonTypePickerParams}
                              options={polygonTypeOptions}
                              onChange={(opt) => {
                                const val = typeof opt === 'object' ? opt?.value : opt;
                                const selected = polygonTypeOptions.find((c) => String(c.value) === String(val)) ?? null;
                                const selectedId = selected ? selected.value : (val ?? null);
                                setFieldValue('polygon_type', selectedId);
                                if (selected) setFieldValue('polygon_type_name', selected.label);
                              }}
                              dialogTitle={t('geofenceList.type', 'Geofence Type')}
                              error={touched.polygon_type && !!errors.polygon_type}
                              helperText={touched.polygon_type && errors.polygon_type}
                            />

                            <TextField
                              fullWidth
                              label={t('geofenceList.remark', 'Remark')}
                              name="remark"
                              value={values.remark ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.remark && Boolean(errors.remark)}
                              helperText={touched.remark && errors.remark}
                            />
                          </Stack>
                        </Box>

                        {/* KOLOM KANAN */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Stack spacing={2}>
                            <TextField
                              label={`${t('geofenceList.address', 'Address')} *`}
                              name="address"
                              value={values.address ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              size="medium"
                              fullWidth
                              multiline
                              minRows={2}
                              error={touched.address && Boolean(errors.address)}
                              helperText={touched.address && errors.address}
                            />

                            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={Boolean(values.status)}
                                    onChange={(e) => setFieldValue('status', e.target.checked)}
                                    name="status"
                                  />
                                }
                                label={t('geofenceList.status', 'Status')}
                              />
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                label={t('geofenceList.drawLocation', 'Draw Location (WKT)')}
                                name="geom"
                                value={values.geom ?? ''}
                                onChange={(e) => {
                                  const formatted = e.target.value.toUpperCase().replace(/[^A-Z0-9\s().,-]/g, '');
                                  setFieldValue('geom', formatted);
                                }}
                                onBlur={handleBlur}
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={5}
                                InputProps={{
                                  readOnly: !isEditingGeom,
                                  sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={() => setIsEditingGeom(!isEditingGeom)}
                                        edge="end"
                                        title={isEditingGeom ? 'Simpan' : 'Edit Manual'}
                                        sx={{
                                          bgcolor: isEditingGeom ? '#e8f5e9' : '#e3f2fd',
                                          color: isEditingGeom ? '#2e7d32' : '#1976d2',
                                          borderRadius: '10px',
                                          p: 0.8,
                                          boxShadow: isEditingGeom
                                            ? '0 2px 6px rgba(46, 125, 50, 0.2)'
                                            : '0 2px 6px rgba(25, 118, 210, 0.2)',
                                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                          '&:hover': {
                                            bgcolor: isEditingGeom ? '#c8e6c9' : '#bbdefb',
                                            transform: 'translateY(-2px)',
                                            boxShadow: isEditingGeom
                                              ? '0 4px 10px rgba(46, 125, 50, 0.3)'
                                              : '0 4px 10px rgba(25, 118, 210, 0.3)'
                                          }
                                        }}
                                      >
                                        {isEditingGeom ? <CheckIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }}
                                helperText={
                                  values.info ? `Saved ${values.info.features?.length ?? 1} feature(s)` : 'There are no polygon yet'
                                }
                              />
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Button
                                variant="outlined"
                                disabled={disabledViewMap}
                                startIcon={<MapIcon />}
                                onClick={() => setMapOpen(true)}
                              >
                                {t('geofenceList.viewMap', 'View Map')}
                              </Button>
                              {disabledViewMap && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  *To change the polygon, click cancel.
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </Box>
                      </Box>
                    </form>
                  </Box>
                )}
              </FormDialog>
            );
          }}
        </Formik>

        {mapOpen && (
          <DrawPolygon
            open={mapOpen}
            onClose={() => setMapOpen(false)}
            title={t('geofenceList.drawLocation', 'Draw Polygon')}
            initialGeoJSON={formikRef.current?.values?.info}
            center={
              formikRef.current?.values?.centroid_lat && formikRef.current?.values?.centroid_lon
                ? [formikRef.current.values.centroid_lat, formikRef.current.values.centroid_lon]
                : undefined
            }
            zoom={formikRef.current?.values?.centroid_lat && formikRef.current?.values?.centroid_lon ? 16 : undefined}
            position="topleft"
            singleFeature={true}
            onSave={(geojson) => {
              if (!formikRef.current) return;
              const { setFieldValue } = formikRef.current;

              if (!geojson) {
                setFieldValue('info', null);
                setFieldValue('geom', null);
                setFieldValue('coords', null);
                setFieldValue('centroid_lat', null);
                setFieldValue('centroid_lon', null);
                setFieldValue('latitude', null);
                setFieldValue('longitude', null);
                return;
              }
              const fc = featureToFeatureCollection(geojson);
              const first = fc.features?.[0] ?? null;
              const wkt = geojsonToWKT(first ?? fc);
              const coordsStr = polygonCoordsStringFromGeoJSON(first ?? fc);
              const centroid = computeCentroidFromGeoJSON(first ?? fc);

              setFieldValue('info', fc);
              setFieldValue('geom', wkt);
              setFieldValue('coords', coordsStr);
              setFieldValue('centroid_lat', centroid.lat);
              setFieldValue('centroid_lon', centroid.lon);
              setFieldValue('latitude', centroid.lat);
              setFieldValue('longitude', centroid.lon);
            }}
          />
        )}
      </>
    );
  }
);

export default MasterGeofenceLibraryAddEdit;
